import { useState, useEffect } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card"
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { Label } from "../components/ui/label"
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar"
import { PageLayout } from "../components/page-layout"
import { useAuth } from "../contexts/auth-context"
import { apiService } from "../lib/api"
import { useToast } from "../hooks/use-toast"
import { TranslatedText } from "../components/translated-text"
import { useTranslation } from "../hooks/useTranslation"
import { User, Mail, Building2, Shield, Save, Lock, Camera, Edit2, X } from "lucide-react"
import { Separator } from "../components/ui/separator"
import { Badge } from "../components/ui/badge"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog"

export default function ProfilePage() {
  const { user, updateUser } = useAuth()
  const { toast } = useToast()
  const { tSync } = useTranslation()
  const queryClient = useQueryClient()
  
  const [isEditing, setIsEditing] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [profileForm, setProfileForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  })
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  })
  const [avatarUrl, setAvatarUrl] = useState<string>("")

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      const nameParts = (user.firstName || "").split(" ")
      setProfileForm({
        firstName: nameParts[0] || "",
        lastName: nameParts.slice(1).join(" ") || user.lastName || "",
        email: user.email || "",
        phone: "",
      })
    }
  }, [user])

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: { firstName: string; lastName: string; email: string }) => {
      // Use the new endpoint for users to update their own profile
      await apiService.updateCurrentUserProfile({
        name: `${data.firstName} ${data.lastName}`.trim(),
        email: data.email,
      })
    },
    onSuccess: () => {
      toast({
        title: tSync("Success"),
        description: tSync("Profile updated successfully"),
      })
      setIsEditing(false)
      queryClient.invalidateQueries({ queryKey: ["user"] })
      // Refresh the page to get updated user data
      window.location.reload()
    },
    onError: (error: any) => {
      toast({
        title: tSync("Error"),
        description: error?.message || tSync("Failed to update profile. Please try again."),
        variant: "destructive",
      })
    },
  })

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      // Use the update profile endpoint with password and current password verification
      await apiService.updateCurrentUserProfile({
        password: data.newPassword,
        currentPassword: data.currentPassword,
      })
    },
    onSuccess: () => {
      toast({
        title: tSync("Success"),
        description: tSync("Password changed successfully"),
      })
      setIsChangingPassword(false)
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      })
    },
    onError: (error: any) => {
      toast({
        title: tSync("Error"),
        description: error?.message || tSync("Failed to change password. Please check your current password."),
        variant: "destructive",
      })
    },
  })

  const handleSaveProfile = () => {
    if (!profileForm.firstName.trim() || !profileForm.email.trim()) {
      toast({
        title: tSync("Validation Error"),
        description: tSync("First name and email are required"),
        variant: "destructive",
      })
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileForm.email)) {
      toast({
        title: tSync("Validation Error"),
        description: tSync("Please enter a valid email address"),
        variant: "destructive",
      })
      return
    }

    updateProfileMutation.mutate({
      firstName: profileForm.firstName,
      lastName: profileForm.lastName,
      email: profileForm.email,
    })
  }

  const handleChangePassword = () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast({
        title: tSync("Validation Error"),
        description: tSync("All password fields are required"),
        variant: "destructive",
      })
      return
    }

    if (passwordForm.newPassword.length < 8) {
      toast({
        title: tSync("Validation Error"),
        description: tSync("Password must be at least 8 characters long"),
        variant: "destructive",
      })
      return
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast({
        title: tSync("Validation Error"),
        description: tSync("New passwords do not match"),
        variant: "destructive",
      })
      return
    }

    changePasswordMutation.mutate({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword,
    })
  }

  const getInitials = () => {
    if (!user) return "U"
    const first = user.firstName?.charAt(0) || ""
    const last = user.lastName?.charAt(0) || ""
    return (first + last).toUpperCase() || user.email?.charAt(0).toUpperCase() || "U"
  }

  // Helper function to display role - admin shows as Super Admin, accountant shows as Admin
  const getDisplayRole = (role: string | undefined): string => {
    if (!role) return 'USER'
    if (role === 'admin') return 'SUPER ADMIN'
    if (role === 'accountant') return 'ADMIN'
    return role.toUpperCase()
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-700"
      case "accountant":
        return "bg-red-100 text-red-700" // Use same color as admin since it displays as admin
      case "auditor":
        return "bg-purple-100 text-purple-700"
      default:
        return "bg-gray-100 text-gray-700"
    }
  }

  if (!user) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">
            <TranslatedText text="Please log in to view your profile." />
          </p>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            <TranslatedText text="Profile" />
          </h1>
          <p className="text-muted-foreground mt-1">
            <TranslatedText text="Manage your account settings and preferences" />
          </p>
        </div>

        {/* Profile Overview Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>
                  <TranslatedText text="Profile Information" />
                </CardTitle>
                <CardDescription>
                  <TranslatedText text="Your personal account details" />
                </CardDescription>
              </div>
              {!isEditing && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  <TranslatedText text="Edit" />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Avatar Section */}
            <div className="flex items-center gap-6">
              <div className="relative">
                <Avatar className="w-24 h-24">
                  <AvatarImage src={avatarUrl} alt={`${user.firstName} ${user.lastName}`} />
                  <AvatarFallback className="text-2xl bg-primary text-primary-foreground">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
                {isEditing && (
                  <Button
                    variant="outline"
                    size="icon"
                    className="absolute bottom-0 right-0 rounded-full w-8 h-8"
                    onClick={() => {
                      // TODO: Implement avatar upload
                      toast({
                        title: tSync("Coming Soon"),
                        description: tSync("Avatar upload will be available soon"),
                      })
                    }}
                  >
                    <Camera className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <div className="flex-1">
                {isEditing ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">
                          <TranslatedText text="First Name" /> *
                        </Label>
                        <Input
                          id="firstName"
                          value={profileForm.firstName}
                          onChange={(e) =>
                            setProfileForm({ ...profileForm, firstName: e.target.value })
                          }
                          placeholder={tSync("Enter your first name")}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">
                          <TranslatedText text="Last Name" />
                        </Label>
                        <Input
                          id="lastName"
                          value={profileForm.lastName}
                          onChange={(e) =>
                            setProfileForm({ ...profileForm, lastName: e.target.value })
                          }
                          placeholder={tSync("Enter your last name")}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">
                        <TranslatedText text="Email" /> *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={profileForm.email}
                        onChange={(e) =>
                          setProfileForm({ ...profileForm, email: e.target.value })
                        }
                        placeholder={tSync("Enter your email")}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">
                        <TranslatedText text="Phone" />
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={profileForm.phone}
                        onChange={(e) =>
                          setProfileForm({ ...profileForm, phone: e.target.value })
                        }
                        placeholder={tSync("Enter your phone number")}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleSaveProfile}
                        disabled={updateProfileMutation.isPending}
                        className="flex items-center gap-2"
                      >
                        <Save className="w-4 h-4" />
                        {updateProfileMutation.isPending ? (
                          <TranslatedText text="Saving..." />
                        ) : (
                          <TranslatedText text="Save Changes" />
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setIsEditing(false)
                          // Reset form to original values
                          const nameParts = (user.firstName || "").split(" ")
                          setProfileForm({
                            firstName: nameParts[0] || "",
                            lastName: nameParts.slice(1).join(" ") || user.lastName || "",
                            email: user.email || "",
                            phone: "",
                          })
                        }}
                      >
                        <X className="w-4 h-4 mr-2" />
                        <TranslatedText text="Cancel" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-xl font-semibold">
                        {user.firstName} {user.lastName}
                      </h3>
                      <p className="text-muted-foreground flex items-center gap-2 mt-1">
                        <Mail className="w-4 h-4" />
                        {user.email}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge className={getRoleBadgeColor(user.role)}>
                        {getDisplayRole(user.role)}
                      </Badge>
                      {user.companyName && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Building2 className="w-4 h-4" />
                          {user.companyName}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Security Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              <TranslatedText text="Account Security" />
            </CardTitle>
            <CardDescription>
              <TranslatedText text="Manage your password and security settings" />
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Lock className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">
                      <TranslatedText text="Password" />
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <TranslatedText text="Last updated: Never" />
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsChangingPassword(true)}
                >
                  <TranslatedText text="Change Password" />
                </Button>
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">
                      <TranslatedText text="Two-Factor Authentication" />
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <TranslatedText text="Add an extra layer of security" />
                    </p>
                  </div>
                </div>
                <Badge variant="outline">
                  <TranslatedText text="Not Enabled" />
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Information Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              <TranslatedText text="Account Information" />
            </CardTitle>
            <CardDescription>
              <TranslatedText text="Additional account details" />
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">
                    <TranslatedText text="User ID" />
                  </Label>
                  <p className="text-sm font-mono mt-1">{user.id}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">
                    <TranslatedText text="Tenant ID" />
                  </Label>
                  <p className="text-sm font-mono mt-1">{user.tenantId}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">
                    <TranslatedText text="Account Created" />
                  </Label>
                  <p className="text-sm mt-1">
                    {user.createdAt
                      ? new Date(user.createdAt).toLocaleDateString()
                      : tSync("N/A")}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">
                    <TranslatedText text="Email Verified" />
                  </Label>
                  <Badge
                    variant={user.isEmailVerified ? "default" : "destructive"}
                    className="mt-1"
                  >
                    {user.isEmailVerified ? (
                      <TranslatedText text="Verified" />
                    ) : (
                      <TranslatedText text="Not Verified" />
                    )}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={isChangingPassword} onOpenChange={setIsChangingPassword}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <TranslatedText text="Change Password" />
            </DialogTitle>
            <DialogDescription>
              <TranslatedText text="Enter your current password and choose a new one" />
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">
                <TranslatedText text="Current Password" /> *
              </Label>
              <Input
                id="currentPassword"
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) =>
                  setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                }
                placeholder={tSync("Enter your current password")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">
                <TranslatedText text="New Password" /> *
              </Label>
              <Input
                id="newPassword"
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) =>
                  setPasswordForm({ ...passwordForm, newPassword: e.target.value })
                }
                placeholder={tSync("Enter your new password")}
              />
              <p className="text-xs text-muted-foreground">
                <TranslatedText text="Password must be at least 8 characters long" />
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">
                <TranslatedText text="Confirm New Password" /> *
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) =>
                  setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                }
                placeholder={tSync("Confirm your new password")}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsChangingPassword(false)
                setPasswordForm({
                  currentPassword: "",
                  newPassword: "",
                  confirmPassword: "",
                })
              }}
            >
              <TranslatedText text="Cancel" />
            </Button>
            <Button
              onClick={handleChangePassword}
              disabled={changePasswordMutation.isPending}
            >
              {changePasswordMutation.isPending ? (
                <TranslatedText text="Changing..." />
              ) : (
                <TranslatedText text="Change Password" />
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  )
}

